import React, { useState, useEffect } from 'react';
import './ImageViewer.css';

interface ImageViewerProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  imageAlt?: string;
}

export const ImageViewer: React.FC<ImageViewerProps> = ({
  isOpen,
  onClose,
  imageUrl,
  imageAlt = 'Imagem'
}) => {
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // Reset scale and position when image changes
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [imageUrl]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      setScale(prev => Math.min(prev * 1.2, 5));
    } else if (e.key === '-') {
      e.preventDefault();
      setScale(prev => Math.max(prev / 1.2, 0.1));
    } else if (e.key === '0') {
      e.preventDefault();
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => Math.max(0.1, Math.min(5, prev * delta)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDoubleClick = () => {
    if (scale > 1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      setScale(2.5);
    }
  };

  return (
    <div 
      className="image-viewer-overlay" 
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="image-viewer-container">
        <button className="image-viewer-close" onClick={onClose}>
          ×
        </button>
        
        {/* Controles de zoom */}
        <div className="image-viewer-controls">
          <button 
            className="zoom-btn"
            onClick={() => setScale(prev => Math.min(prev * 1.2, 5))}
            title="Zoom In (+)"
          >
            +
          </button>
          <button 
            className="zoom-btn"
            onClick={() => setScale(prev => Math.max(prev / 1.2, 0.1))}
            title="Zoom Out (-)"
          >
            −
          </button>
          <button 
            className="zoom-btn"
            onClick={() => {
              setScale(1);
              setPosition({ x: 0, y: 0 });
            }}
            title="Reset (0)"
          >
            0
          </button>
        </div>

        <div 
          className="image-viewer-content"
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <img 
            src={imageUrl} 
            alt={imageAlt}
            className="image-viewer-image"
            style={{
              transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
              cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in'
            }}
            onError={(e) => {
            }}
            onDoubleClick={handleDoubleClick}
            draggable={false}
          />
        </div>
      </div>
    </div>
  );
};
